import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BulkUploadModal from '../components/BulkUploadModal';
import PageContainer from '../components/PageContainer';
import { logout, isSuperuser } from '../api/auth';

interface Column {
  key: string;
  label: string;
  render?: (value: any, item: any) => React.ReactNode;
}

export interface DataConfig {
  title: string;
  singularName: string;
  columns: Column[];
  fetchData: (page: number, pageSize: number, search?: string, extraParams?: any) => Promise<any>;
  deleteItem: (id: number) => Promise<void>;
  bulkDelete: (ids: number[]) => Promise<any>;
  bulkUpload?: (file: File) => Promise<any>;
  createRoute?: string;
  editRoute?: (id: number) => string;
  extraFilters?: React.ReactNode;
}

interface DataListProps {
  config: DataConfig;
}

const DataList: React.FC<DataListProps> = ({ config }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 50;
  // Writes are superuser-only on the server; hide the controls instead of
  // letting a domain-scoped editor hit a 403.
  const canWrite = isSuperuser();

  const handleApiError = (err: any, fallbackMsg: string): string => {
    if (err?.response?.status === 401) {
      logout();
      navigate('/login');
      return 'Session expired. Please log in again.';
    }
    return err?.response?.data?.detail || err?.message || fallbackMsg;
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await config.fetchData(page, pageSize, search || undefined);
      setItems(result.items);
      setTotal(result.total);
      setTotalPages(result.pages);
    } catch (err: any) {
      const msg = handleApiError(err, 'Failed to load data');
      console.error('Failed to load data:', err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete ${config.singularName} "${name}"?`)) {
      return;
    }

    try {
      await config.deleteItem(id);
      await loadData();
    } catch (err: any) {
      alert('Failed to delete: ' + handleApiError(err, 'Unknown error'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      alert('No items selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedIds.size} selected items?`)) {
      return;
    }

    try {
      const result = await config.bulkDelete(Array.from(selectedIds));
      
      if (result.errors.length > 0) {
        alert(`Deleted ${result.deleted} items. Errors:\n${result.errors.join('\n')}`);
      } else {
        alert(`Successfully deleted ${result.deleted} items`);
      }
      
      setSelectedIds(new Set());
      await loadData();
    } catch (err: any) {
      alert('Bulk delete failed: ' + handleApiError(err, 'Unknown error'));
    }
  };

  const handleBulkUploadSuccess = async () => {
    setShowBulkUpload(false);
    await loadData();
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(item => item.id)));
    }
  };

  const toggleSelect = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  return (
    <PageContainer
      title={config.title}
      subtitle={`${total} ${total === 1 ? 'item' : 'items'} total`}
      actions={canWrite ? (
        <>
          {config.bulkUpload && <button
            onClick={() => setShowBulkUpload(true)}
            className="btn-secondary gap-1.5 text-xs"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            Bulk Upload
          </button>}
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} className="btn-danger gap-1.5 text-xs">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete ({selectedIds.size})
            </button>
          )}
          {config.createRoute && <Link to={config.createRoute} className="btn-primary gap-1.5 text-xs">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Create New
          </Link>}
        </>
      ) : undefined}
    >

      {/* Search */}
      <div className="mb-5">
        <div className="relative max-w-md">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-400 pointer-events-none" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={`Search ${config.title.toLowerCase()}...`}
            className="input-base pl-9"
          />
        </div>
      </div>

      {/* Table */}
      {error && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100">
          <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <div className="flex-1">
            <h4 className="text-xs font-semibold text-red-800">Failed to load data</h4>
            <p className="text-xs text-red-600 mt-0.5">{error}</p>
          </div>
          <button onClick={loadData} className="btn-secondary text-xs px-2.5 py-1">Retry</button>
        </div>
      )}
      {loading ? (
        <div className="card p-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-primary-500">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Loading...
          </div>
        </div>
      ) : items.length === 0 && !error ? (
        <div className="card p-12 text-center">
          <svg className="w-12 h-12 mx-auto text-primary-300 mb-3" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
          </svg>
          <p className="text-sm text-primary-500 mb-4">No items found</p>
          {canWrite && config.createRoute && <Link to={config.createRoute} className="btn-primary text-xs px-3 py-2">Create your first {config.singularName.toLowerCase()}</Link>}
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-primary-100">
                    {canWrite && (
                      <th className="px-4 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.size === items.length && items.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-primary-300 text-primary-700 focus:ring-primary-200 focus:ring-offset-0"
                        />
                      </th>
                    )}
                    {config.columns.map((col) => (
                      <th
                        key={col.key}
                        className="px-4 py-3 text-left text-xs font-semibold text-primary-500 uppercase tracking-wider"
                      >
                        {col.label}
                      </th>
                    ))}
                    {canWrite && (
                      <th className="px-4 py-3 text-right text-xs font-semibold text-primary-500 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-primary-50">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-primary-50/50 transition-colors">
                      {canWrite && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(item.id)}
                            onChange={() => toggleSelect(item.id)}
                            className="rounded border-primary-300 text-primary-700 focus:ring-primary-200 focus:ring-offset-0"
                          />
                        </td>
                      )}
                      {config.columns.map((col) => (
                        <td key={col.key} className="px-4 py-3 text-sm text-primary-700">
                          {col.render ? col.render(item[col.key], item) : (item[col.key] || <span className="text-primary-300">--</span>)}
                        </td>
                      ))}
                      {canWrite && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {config.editRoute && <Link
                              to={config.editRoute(item.id)}
                              className="btn-ghost text-xs px-2 py-1 text-primary-700 hover:text-primary-800"
                            >
                              Edit
                            </Link>}
                            <button
                              onClick={() => handleDelete(item.id, item.name_ar || item.unit || item.id)}
                              className="btn-ghost text-xs px-2 py-1 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-primary-500">
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary text-xs px-3 py-1.5"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-xs text-primary-500">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary text-xs px-3 py-1.5"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showBulkUpload && config.bulkUpload && (
        <BulkUploadModal
          title={`Bulk Upload ${config.title}`}
          onClose={() => setShowBulkUpload(false)}
          onUpload={config.bulkUpload}
          onSuccess={handleBulkUploadSuccess}
        />
      )}
    </PageContainer>
  );
};

export default DataList;
